import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";

export default function GoodsItemsSection({
  selectedHouse,
  packageTypes,
  handleChangeRows,
  goodsErr,
  handleDeleteRow,
  handleAddRow,
  setIsDirty,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // xs/sm → cards; md+ → table

  const goods = selectedHouse?.goodsList ?? [];

  const sanitize = (v) =>
    v?.replace(
      /[\u00A0\u2007\u202F\u00A0\u1680\u180E\u2000-\u200D\u202F\u205F\u2060\u3000\uFEFF]/g,
      " ",
    );

  return (
    <Grid item xs={12}>
      <Paper elevation={3} sx={{ p: { xs: 1.5, sm: 2 }, minHeight: 150 }}>
        <Grid item xs={12} sx={{ mb: 1 }}>
          <Typography variant="h6" gutterBottom>
            Goods Items
          </Typography>
        </Grid>

        {/* ======== Mobile / Small: Card layout ======== */}
        {isMobile && (
          <Stack spacing={1.25}>
            {goods.map((row, index) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{ p: 1.25, borderRadius: 2 }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2">Item #{index + 1}</Typography>
                  <IconButton
                    color="error"
                    onClick={() => {
                      handleDeleteRow(index);
                      setIsDirty(true);
                    }}
                    size="small"
                    aria-label={`Delete goods item ${index + 1}`}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </Stack>

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      label="No. packages"
                      type="number"
                      inputMode="numeric"
                      value={row.numberOfPackages ?? ""}
                      onChange={(e) => {
                        handleChangeRows(
                          index,
                          "numberOfPackages",
                          e.target.value,
                        );
                        setIsDirty(true);
                      }}
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={goodsErr(index, "numberOfPackages") !== undefined}
                      helperText={goodsErr(index, "numberOfPackages")}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Gross weight (kg)"
                      type="number"
                      inputMode="decimal"
                      inputProps={{ step: "any", min: 0 }}
                      value={row.weight ?? ""}
                      onChange={(e) => {
                        handleChangeRows(index, "weight", e.target.value);
                        setIsDirty(true);
                      }}
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={goodsErr(index, "weight") !== undefined}
                      helperText={goodsErr(index, "weight")}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      size="small"
                      value={row.packageType || null}
                      onChange={(_e, v) => {
                        handleChangeRows(index, "packageType", v);
                        setIsDirty(true);
                      }}
                      inputValue={row.packageTypeSearch || ""}
                      onInputChange={(_e, v) =>
                        handleChangeRows(index, "packageTypeSearch", v)
                      }
                      options={packageTypes}
                      getOptionLabel={(o) => `${o.code} - ${o.name}`}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Package type"
                          variant="outlined"
                          size="small"
                          error={goodsErr(index, "packageType") !== undefined}
                          helperText={goodsErr(index, "packageType")}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="HS-code (6 char.)"
                      value={row.hsCode ?? ""}
                      onChange={(e) => {
                        handleChangeRows(
                          index,
                          "hsCode",
                          e.target.value.replace(/[^a-zA-Z0-9]/g, ""),
                        );
                        setIsDirty(true);
                      }}
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={goodsErr(index, "hsCode") !== undefined}
                      helperText={goodsErr(index, "hsCode")}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="UN Dangerous Goods Code (optional)"
                      value={row.dgCode || ""}
                      onChange={(e) => {
                        handleChangeRows(index, "dgCode", e.target.value);
                        setIsDirty(true);
                      }}
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={goodsErr(index, "dgCode") !== undefined}
                      helperText={goodsErr(index, "dgCode")}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Goods description"
                      value={row.description ?? ""}
                      onChange={(e) => {
                        handleChangeRows(
                          index,
                          "description",
                          sanitize(e.target.value),
                        );
                        setIsDirty(true);
                      }}
                      variant="outlined"
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      error={goodsErr(index, "description") !== undefined}
                      helperText={goodsErr(index, "description")}
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        )}

        {/* ======== Desktop / Medium+: Table layout ======== */}
        {!isMobile && (
          <Box sx={{ mt: 1 }}>
            <TableContainer
              sx={{
                maxHeight: 420,
                overflowX: "auto",
                borderRadius: 2,
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ border: "none", width: 60 }}>
                      No.
                    </TableCell>
                    <TableCell sx={{ border: "none", width: 140 }}>
                      No. packages
                      <Tooltip
                        arrow
                        placement="top"
                        title={
                          "The total number of packages or units included in the shipment."
                        }
                      >
                        <Icon
                          fontSize="small"
                          color="action"
                          sx={{ ml: 0.5, verticalAlign: "middle" }}
                        >
                          help_outline
                        </Icon>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ border: "none", width: 220 }}>
                      Package type
                      <Tooltip
                        arrow
                        placement="top"
                        title={
                          "The type or form of packaging used for the goods."
                        }
                      >
                        <Icon
                          fontSize="small"
                          color="action"
                          sx={{ ml: 0.5, verticalAlign: "middle" }}
                        >
                          help_outline
                        </Icon>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ border: "none", width: 160 }}>
                      HS-code (6 char.)
                      <Tooltip
                        arrow
                        placement="top"
                        title={"Enter the commodity code. "}
                      >
                        <Icon
                          fontSize="small"
                          color="action"
                          sx={{ ml: 0.5, verticalAlign: "middle" }}
                        >
                          help_outline
                        </Icon>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ border: "none", width: 200 }}>
                      UN Dangerous Goods Code
                      <Tooltip
                        arrow
                        placement="top"
                        title={
                          "The United Nations Dangerous Goods identifier (UNDG) is the serial number assigned within the United Nations to substances and articles contained in a list of the dangerous goods most commonly carried. "
                        }
                      >
                        <Icon
                          fontSize="small"
                          color="action"
                          sx={{ ml: 0.5, verticalAlign: "middle" }}
                        >
                          help_outline
                        </Icon>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ border: "none", minWidth: 320 }}>
                      Goods description
                      <Tooltip
                        arrow
                        placement="top"
                        title={
                          "Language description that is precise enough for Customs services to be able to identify the goods. General terms (i.e. 'consolidated', 'general cargo' 'parts' or 'freight of all kinds') or not sufficiently precise description cannot be accepted. A non-exhaustive list of such general terms and descriptions is published by the Commission. "
                        }
                      >
                        <Icon
                          fontSize="small"
                          color="action"
                          sx={{ ml: 0.5, verticalAlign: "middle" }}
                        >
                          help_outline
                        </Icon>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ border: "none", width: 160 }}>
                      Gross weight (kg)
                      <Tooltip
                        arrow
                        placement="top"
                        title={
                          "The total weight of the goods including their packaging, measured in kilograms."
                        }
                      >
                        <Icon
                          fontSize="small"
                          color="action"
                          sx={{ ml: 0.5, verticalAlign: "middle" }}
                        >
                          help_outline
                        </Icon>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ border: "none", width: 72 }} />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {goods.map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell sx={{ border: "none" }}>{index + 1}</TableCell>

                      <TableCell sx={{ border: "none" }}>
                        <TextField
                          type="number"
                          inputMode="numeric"
                          value={row.numberOfPackages ?? ""}
                          onChange={(e) => {
                            handleChangeRows(
                              index,
                              "numberOfPackages",
                              e.target.value,
                            );
                            setIsDirty(true);
                          }}
                          variant="outlined"
                          size="small"
                          error={
                            goodsErr(index, "numberOfPackages") !== undefined
                          }
                          helperText={goodsErr(index, "numberOfPackages")}
                        />
                      </TableCell>

                      <TableCell sx={{ border: "none" }}>
                        <Autocomplete
                          size="small"
                          value={row.packageType || null}
                          onChange={(_event, newValue) => {
                            handleChangeRows(index, "packageType", newValue);
                            setIsDirty(true);
                          }}
                          inputValue={row.packageTypeSearch || ""}
                          onInputChange={(_event, newInputValue) => {
                            handleChangeRows(
                              index,
                              "packageTypeSearch",
                              newInputValue,
                            );
                          }}
                          options={packageTypes}
                          getOptionLabel={(option) =>
                            `${option.code} - ${option.name}`
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Package type"
                              variant="outlined"
                              size="small"
                              error={
                                goodsErr(index, "packageType") !== undefined
                              }
                              helperText={goodsErr(index, "packageType")}
                              sx={{ minWidth: 220 }}
                            />
                          )}
                        />
                      </TableCell>

                      <TableCell sx={{ border: "none" }}>
                        <TextField
                          value={row.hsCode ?? ""}
                          onChange={(e) => {
                            handleChangeRows(
                              index,
                              "hsCode",
                              e.target.value.replace(/[^a-zA-Z0-9]/g, ""),
                            );
                            setIsDirty(true);
                          }}
                          variant="outlined"
                          size="small"
                          error={goodsErr(index, "hsCode") !== undefined}
                          helperText={goodsErr(index, "hsCode")}
                        />
                      </TableCell>

                      <TableCell sx={{ border: "none" }}>
                        <TextField
                          value={row.dgCode || ""}
                          fullWidth
                          placeholder="UN code (optional)"
                          onChange={(e) => {
                            handleChangeRows(index, "dgCode", e.target.value);
                            setIsDirty(true);
                          }}
                          variant="outlined"
                          size="small"
                          error={goodsErr(index, "dgCode") !== undefined}
                          helperText={goodsErr(index, "dgCode")}
                        />
                      </TableCell>

                      <TableCell sx={{ border: "none" }}>
                        <TextField
                          value={row.description ?? ""}
                          fullWidth
                          onChange={(e) => {
                            handleChangeRows(
                              index,
                              "description",
                              sanitize(e.target.value),
                            );
                            setIsDirty(true);
                          }}
                          variant="outlined"
                          size="small"
                          error={goodsErr(index, "description") !== undefined}
                          helperText={goodsErr(index, "description")}
                        />
                      </TableCell>

                      <TableCell sx={{ border: "none" }}>
                        <TextField
                          type="number"
                          inputMode="decimal"
                          inputProps={{ step: "any", min: 0 }}
                          value={row.weight ?? ""}
                          onChange={(e) => {
                            handleChangeRows(
                              index,
                              "weight",
                              e.target.value,
                            );
                            setIsDirty(true);
                          }}
                          error={goodsErr(index, "weight") !== undefined}
                          helperText={goodsErr(index, "weight")}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>

                      <TableCell sx={{ border: "none" }}>
                        <IconButton
                          color="error"
                          onClick={() => {
                            handleDeleteRow(index);
                            setIsDirty(true);
                          }}
                          aria-label={`Delete goods item ${index + 1}`}
                        >
                          <Icon>delete</Icon>
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Box
          display="flex"
          justifyContent={{ xs: "stretch", sm: "flex-start" }}
        >
          <Button
            onClick={() => {
              handleAddRow();
              setIsDirty(true);
            }}
            variant="text"
            size="small"
            color="info"
            startIcon={<Icon>add</Icon>}
            fullWidth={isMobile}
          >
            Add goods
          </Button>
        </Box>
      </Paper>
    </Grid>
  );
}
